/**
 * Security utilities for AI Flow
 * Implements industry-standard protections against XSS, injection, and other attacks
 */

// HTML entities for XSS prevention
const HTML_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;'
};

/**
 * Sanitize HTML to prevent XSS attacks
 * @param {string} input - Raw HTML input
 * @returns {string} - Sanitized HTML
 */
export function sanitizeHtml(input) {
  if (typeof input !== 'string') return '';
  return input.replace(/[&<>"'`=\/]/g, (s) => HTML_ENTITIES[s]);
}

/**
 * Validate and sanitize variable names
 * @param {string} name - Variable name
 * @returns {string} - Sanitized variable name
 */
export function sanitizeVariableName(name) {
  if (typeof name !== 'string') return '';
  // Allow only alphanumeric, underscore, and dot (for object paths)
  return name.replace(/[^a-zA-Z0-9_.]/g, '').substring(0, 100);
}

/**
 * Sanitize variable values to prevent injection
 * @param {any} value - Variable value
 * @returns {any} - Sanitized value
 */
export function sanitizeVariableValue(value) {
  if (typeof value === 'string') {
    // Limit string length and sanitize HTML
    return sanitizeHtml(value.substring(0, 10000));
  }
  if (typeof value === 'number') {
    // Ensure finite numbers
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === null || value === undefined) {
    return value;
  }
  // For objects/arrays, stringify and limit size
  try {
    const str = JSON.stringify(value);
    if (str.length > 50000) {
      throw new Error('Value too large');
    }
    return JSON.parse(str); // Re-parse to ensure clean object
  } catch {
    return '[Invalid Value]';
  }
}

// Same as sanitizeVariableValue but without HTML-escaping — for a variable
// that feeds plain-text UI (PreviewPlayer's Test chat, VariableContext's
// display-facing interpolation) rather than an actual HTML sink. There is
// no dangerouslySetInnerHTML/innerHTML/document.write anywhere in src/, so
// escaping there never protected anything — it only baked entities like
// &#x27; into what the tester/visitor actually sees. Kept separate from
// sanitizeVariableValue rather than changing it in place: worker/src/
// variables.js's escaping resolveTemplate() (still correct for its own
// composio-tool-arg and AI-prompt-construction call sites) imports and
// depends on sanitizeVariableValue's escaping behavior. Mirrors the
// already-shipped resolveTemplate → resolveTemplateRaw split in that file.
export function sanitizeVariableValueRaw(value) {
  if (typeof value === 'string') return value.substring(0, 10000);
  return sanitizeVariableValue(value);
}

/**
 * Safe template variable replacement with XSS protection
 * @param {string} template - Template string with {{variable}} placeholders
 * @param {Object} variables - Variable values
 * @param {string} nodeId - Node ID for scoped variables
 * @returns {string} - Safe rendered template
 */
export function safeReplaceVariables(template, variables = {}, nodeId = null) {
  if (typeof template !== 'string') return '';

  return template.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
    const trimmedName = sanitizeVariableName(variableName.trim());
    if (!trimmedName) return match;

    // Get variable value safely
    let value = variables[trimmedName];
    if (value === undefined && nodeId) {
      value = variables[`${nodeId}_${trimmedName}`];
    }

    if (value === undefined) return match;

    // Sanitize the value before insertion
    const sanitizedValue = sanitizeVariableValue(value);
    return typeof sanitizedValue === 'string'
      ? sanitizeHtml(sanitizedValue)
      : String(sanitizedValue);
  });
}

// Non-escaping counterpart to safeReplaceVariables — see
// sanitizeVariableValueRaw's comment for why. Used by VariableContext's
// display-facing replaceVariables (PreviewPlayer's Test chat).
export function safeReplaceVariablesRaw(template, variables = {}, nodeId = null) {
  if (typeof template !== 'string') return '';

  return template.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
    const trimmedName = sanitizeVariableName(variableName.trim());
    if (!trimmedName) return match;

    let value = variables[trimmedName];
    if (value === undefined && nodeId) {
      value = variables[`${nodeId}_${trimmedName}`];
    }

    if (value === undefined) return match;

    const sanitizedValue = sanitizeVariableValueRaw(value);
    return typeof sanitizedValue === 'string'
      ? sanitizedValue
      : String(sanitizedValue);
  });
}

/**
 * Validate flow structure to prevent malicious flows
 * @param {Object} flow - Flow object with nodes and edges
 * @returns {Object} - Validation result
 */
export function validateFlow(flow) {
  const errors = [];
  const warnings = [];
  
  if (!flow || typeof flow !== 'object') {
    errors.push('Invalid flow structure');
    return { valid: false, errors, warnings };
  }
  
  const { nodes = [], edges = [] } = flow;
  
  // Validate nodes
  if (!Array.isArray(nodes)) {
    errors.push('Nodes must be an array');
  } else {
    if (nodes.length > 1000) {
      errors.push('Too many nodes (max 1000)');
    }
    
    nodes.forEach((node, index) => {
      if (!node.id || typeof node.id !== 'string') {
        errors.push(`Node ${index}: Invalid ID`);
      }
      if (!node.type || typeof node.type !== 'string') {
        errors.push(`Node ${index}: Invalid type`);
      }
      if (node.id && node.id.length > 100) {
        errors.push(`Node ${index}: ID too long`);
      }
    });
  }
  
  // Validate edges
  if (!Array.isArray(edges)) {
    errors.push('Edges must be an array');
  } else {
    if (edges.length > 2000) {
      errors.push('Too many edges (max 2000)');
    }
    
    edges.forEach((edge, index) => {
      if (!edge.source || !edge.target) {
        errors.push(`Edge ${index}: Missing source or target`);
      }
    });
  }
  
  // Check for cycles that could cause infinite loops
  const nodeIds = new Set(nodes.map(n => n.id));
  const hasValidReferences = edges.every(e => 
    nodeIds.has(e.source) && nodeIds.has(e.target)
  );
  
  if (!hasValidReferences) {
    errors.push('Invalid node references in edges');
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Rate limiting for API calls
 */
class RateLimiter {
  constructor(maxRequests = 100, windowMs = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.requests = new Map();
  }
  
  isAllowed(identifier) {
    const now = Date.now();
    const windowStart = now - this.windowMs;
    
    // Clean old requests
    if (this.requests.has(identifier)) {
      const userRequests = this.requests.get(identifier);
      const validRequests = userRequests.filter(time => time > windowStart);
      this.requests.set(identifier, validRequests);
    }
    
    const currentRequests = this.requests.get(identifier) || [];
    
    if (currentRequests.length >= this.maxRequests) {
      return false;
    }
    
    currentRequests.push(now);
    this.requests.set(identifier, currentRequests);
    return true;
  }
}

export const flowExecutionLimiter = new RateLimiter(50, 60000); // 50 executions per minute
export const embedLoadLimiter = new RateLimiter(200, 60000); // 200 embed loads per minute

/**
 * Validate embed configuration to prevent malicious embeds
 * @param {Object} config - Embed configuration
 * @returns {Object} - Validation result
 */
export function validateEmbedConfig(config) {
  const errors = [];
  
  if (!config || typeof config !== 'object') {
    errors.push('Invalid embed configuration');
    return { valid: false, errors };
  }
  
  // Validate domains
  if (config.allowedDomains) {
    const domains = config.allowedDomains.split(',').map(d => d.trim());
    const invalidDomains = domains.filter(domain => {
      if (!domain) return false;
      // Basic domain validation
      return !/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain) || domain.length > 100;
    });
    if (invalidDomains.length > 0) {
      errors.push(`Invalid domains: ${invalidDomains.join(', ')}`);
    }
  }
  
  // Validate title
  if (config.title && (typeof config.title !== 'string' || config.title.length > 200)) {
    errors.push('Invalid title');
  }
  
  // Validate color
  if (config.color && !/^#[0-9A-Fa-f]{6}$/.test(config.color)) {
    errors.push('Invalid color format');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Content Security Policy headers for embed security
 */
export const CSP_HEADERS = {
  'Content-Security-Policy': [
    "default-src 'self'",
    "script-src 'self' 'unsafe-inline'", // Needed for embed scripts
    "style-src 'self' 'unsafe-inline'",
    "img-src 'self' data: https:",
    "connect-src 'self' https:",
    "font-src 'self' https:",
    "frame-ancestors 'none'", // Prevent clickjacking
    "base-uri 'self'",
    "form-action 'self'"
  ].join('; ')
};

/**
 * Security headers for production deployment
 */
export const SECURITY_HEADERS = {
  ...CSP_HEADERS,
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'geolocation=(), microphone=(), camera=(), payment=()'
};
